import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bed, Layers } from "lucide-react";
import { Button, Dialog, Field, FieldError, FieldLabel, Input, NumberInput } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import {
  useCreateHotelRoom,
  useUpdateHotelRoom,
  type HotelRoom,
  type HotelSpaceType,
} from "@/hooks/useHotelRooms";
import { ColorSwatchPicker } from "./primitives";

export default function RoomDialog({
  open,
  onOpenChange,
  room,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` opens the dialog in create mode. */
  room: HotelRoom | null;
}) {
  const { t } = useTranslation("daycareHotel");
  const create = useCreateHotelRoom();
  const update = useUpdateHotelRoom();

  const [name, setName] = useState("");
  const [color, setColor] = useState<string | null>("purple");
  const [spaceType, setSpaceType] = useState<HotelSpaceType>("bed");
  const [capacity, setCapacity] = useState<number | null>(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(room?.name ?? "");
    setColor(room?.color ?? "purple");
    setSpaceType(room?.spaceType ?? "bed");
    setCapacity(room?.capacity ?? 1);
    setError(null);
  }, [open, room]);

  const isSaving = create.isPending || update.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("hotel.errorNameRequired"));
      return;
    }
    // hotel_rooms_capacity_chk requires a capacity >= 1 on `capacity` rooms.
    if (spaceType === "capacity" && (!capacity || capacity < 1)) {
      setError(t("hotel.errorCapacityRequired"));
      return;
    }

    const input = { name: trimmed, color, spaceType, capacity };
    try {
      if (room) await update.mutateAsync({ id: room.id, ...input });
      else await create.mutateAsync(input);
      toast({ title: room ? t("hotel.toastUpdated") : t("hotel.toastCreated") });
      onOpenChange(false);
    } catch {
      toast({ variant: "destructive", title: t("hotel.toastSaveFailed") });
    }
  };

  const typeOptions = [
    { value: "bed" as const, icon: Bed, labelKey: "hotel.typeBed", hintKey: "hotel.typeBedHint" },
    { value: "capacity" as const, icon: Layers, labelKey: "hotel.typeCapacity", hintKey: "hotel.typeCapacityHint" },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={room ? t("hotel.dialogEditTitle") : t("hotel.dialogCreateTitle")}
      footer={
        <div className="flex w-full gap-3">
          <Button type="button" variant="outline" fullWidth onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="hotel-room-form" fullWidth disabled={isSaving}>
            {isSaving ? t("common.saving") : t("hotel.save")}
          </Button>
        </div>
      }
    >
      <form id="hotel-room-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Field invalid={!!error}>
          <FieldLabel>{t("hotel.nameLabel")}</FieldLabel>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("hotel.namePlaceholder")}
            autoFocus
          />
        </Field>

        <div>
          <span className="akuna-field__label mb-2 block">{t("hotel.colorLabel")}</span>
          <ColorSwatchPicker value={color} onChange={setColor} label={t("hotel.colorLabel")} />
        </div>

        <div>
          <span className="akuna-field__label mb-2 block">{t("hotel.typeLabel")}</span>
          <div role="radiogroup" aria-label={t("hotel.typeLabel")} className="grid gap-2.5 sm:grid-cols-2">
            {typeOptions.map((option) => {
              const OptionIcon = option.icon;
              const selected = spaceType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setSpaceType(option.value)}
                  className={cn(
                    "flex flex-col items-start gap-1 rounded-xl border px-3.5 py-3 text-left transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    selected
                      ? "border-akuna-purple bg-purple-50"
                      : "border-border-gray bg-card hover:bg-background-app",
                  )}
                >
                  <span className="flex items-center gap-2 text-[13.5px] font-semibold text-foreground">
                    <OptionIcon
                      className={cn("h-4 w-4", selected ? "text-akuna-purple" : "text-muted-foreground")}
                      aria-hidden="true"
                    />
                    {t(option.labelKey)}
                  </span>
                  <span className="text-xs text-muted-foreground">{t(option.hintKey)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {spaceType === "capacity" ? (
          <Field>
            <FieldLabel>{t("hotel.capacityLabel")}</FieldLabel>
            <NumberInput value={capacity ?? undefined} onValueChange={setCapacity} min={1} className="w-28" />
          </Field>
        ) : (
          <p className="rounded-xl bg-background-app px-3.5 py-3 text-xs text-muted-foreground">
            {t("hotel.bedsAfterSaveHint")}
          </p>
        )}

        {error ? <FieldError>{error}</FieldError> : null}
      </form>
    </Dialog>
  );
}
